import { Directive, ElementRef, Input, OnChanges, Renderer2, SimpleChanges, AfterViewInit } from '@angular/core';

/** Shape of the skeleton loader */
type SkeletonShape = 'pill' | 'rectangle' | 'donut';

/** Width of the skeleton - can be a single value or array of values for animation */
type SkeletonWidth = string | string[];

/** Height of the skeleton */
type SkeletonHeight = string;

type SkeletonTop = string;

/**
 * Configuration object for skeleton properties.
 * This allows setting multiple skeleton properties in a single object.
 * Individual inputs will override config properties when both are provided.
 */
interface SkeletonConfig {
  /** Shape of the skeleton loader. Overrides skeletonShape input when provided */
  shape?: SkeletonShape;

  /** Width of the skeleton. Can be single value or array for animation. Overrides skeletonWidth input when provided */
  width?: SkeletonWidth;

  /** Height of the skeleton. Overrides skeletonHeight input when provided */
  height?: SkeletonHeight;

  /** Top position of the skeleton. Overrides skeletonTop input when provided */
  top?: SkeletonTop;
}

/**
 * Skeleton loading directive that provides visual placeholders while content is loading.
 *
 * @example
 * Basic usage:
 * ```html
 * <div [sgSkeleton]="isLoading" skeletonWidth="200px" skeletonHeight="20px">
 *   {{ content }}
 * </div>
 * ```
 *
 * @example
 * Using config object:
 * ```html
 * <div [sgSkeleton]="isLoading" [skeletonConfig]="{ width: '200px', height: '20px', shape: 'rectangle' }">
 *   {{ content }}
 * </div>
 * ```
 *
 * @example
 * Animated width:
 * ```html
 * <div [sgSkeleton]="isLoading" [skeletonWidth]="['100px', '150px', '120px']">
 *   {{ content }}
 * </div>
 * ```
 */
@Directive({
  selector: '[sgSkeleton]',
  host: {
    '[class.sg-skeleton]': 'sgSkeleton',
  },
  standalone: false,
})
export class SgSkeletonDirective implements OnChanges, AfterViewInit {
  /**
   * Controls whether the skeleton is enabled or disabled.
   * When true, shows skeleton placeholder. When false, shows actual content.
   *
   * @default true
   */
  @Input() sgSkeleton = true;

  /**
   * Shape of the skeleton loader.
   * - 'pill': Rounded rectangle (default)
   * - 'rectangle': Sharp corners
   * - 'donut': Circular shape
   *
   * Can be overridden by skeletonConfig.shape
   *
   * @default 'pill'
   */
  @Input() skeletonShape: SkeletonShape = 'pill';

  /**
   * Width of the skeleton.
   * Can be a single CSS value (e.g., '200px', '50%', '10ch') or an array of values for animation.
   * When an array is provided, the width will cycle through the values every second.
   *
   * Can be overridden by skeletonConfig.width
   *
   * @example
   * Single width: skeletonWidth="200px"
   * Animated width: [skeletonWidth]="['100px', '150px', '120px']"
   *
   * @default ''
   */
  @Input() skeletonWidth: SkeletonWidth = '';

  /**
   * Height of the skeleton.
   * Should be a CSS value (e.g., '20px', '2em', '100%').
   *
   * Can be overridden by skeletonConfig.height
   */
  @Input() skeletonHeight: SkeletonHeight;

  /**
   * Top position of the skeleton.
   * Should be a CSS value (e.g., '0', '10px', '2em').
   *
   * Can be overridden by skeletonConfig.top
   */
  @Input() skeletonTop: SkeletonTop;

  /**
   * Configuration object for skeleton properties.
   * Provides an alternative way to set skeleton properties using a single object.
   * Individual inputs (skeletonShape, skeletonWidth, skeletonHeight) will override
   * corresponding config properties when both are provided.
   *
   * Note: The enabled state is controlled by the main sgSkeleton input, not by this config.
   *
   * @example
   * ```html
   * [skeletonConfig]="{
   *   shape: 'rectangle',
   *   width: '200px',
   *   height: '20px',
   *   top: '0'
   * }"
   * ```
   *
   * @example
   * Config with individual override:
   * ```html
   * [skeletonConfig]="{ width: '100px', height: '20px' }"
   * skeletonShape="donut"
   * <!-- shape will be 'donut', overriding any shape in config -->
   * ```
   *
   * @default {}
   */
  @Input() skeletonConfig: SkeletonConfig = {};

  private widthAnimationInterval?: number;
  private currentWidthIndex = 0;
  private viewInitialized = false;
  private readonly placeholderElement: HTMLElement;

  private originalWidth?: string;
  private originalHeight?: string;
  private originalTop?: string;
  private skeletonWidthApplied = false;
  private skeletonHeightApplied = false;
  private skeletonTopApplied = false;

  // Computed properties based on merged config

  /** Returns the effective enabled state (always uses sgSkeleton input) */
  protected get effectiveEnabled(): boolean {
    return this.sgSkeleton;
  }

  /** Returns the effective shape (config overrides individual input) */
  private get effectiveShape(): SkeletonShape {
    return this.skeletonConfig?.shape ?? this.skeletonShape;
  }

  /** Returns the effective width (config overrides individual input) */
  private get effectiveWidth(): SkeletonWidth {
    return this.skeletonConfig?.width ?? this.skeletonWidth;
  }

  /** Returns the effective height (config overrides individual input) */
  private get effectiveHeight(): SkeletonHeight | undefined {
    return this.skeletonConfig?.height ?? this.skeletonHeight;
  }

  private get effectiveTop(): SkeletonTop | undefined {
    return this.skeletonConfig?.top ?? this.skeletonTop;
  }

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {
    this.placeholderElement = this.renderer.createElement('span');
    this.renderer.setProperty(this.placeholderElement, 'innerHTML', '&#8203;');
    this.renderer.addClass(this.placeholderElement, 'skeleton-placeholder');
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.updateSkeletonState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to changes in any skeleton-related inputs
    if (
      changes['sgSkeleton'] ||
      changes['skeletonShape'] ||
      changes['skeletonWidth'] ||
      changes['skeletonHeight'] ||
      changes['skeletonConfig']
    ) {
      if (this.viewInitialized) {
        this.updateSkeletonState();
      }
    }
  }

  /** Updates the skeleton state based on current inputs */
  private updateSkeletonState(): void {
    if (this.effectiveEnabled) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  /** Activates the skeleton by applying styles and adding placeholder content */
  private activate(): void {
    this.originalWidth = this.el.nativeElement.style.width || undefined;
    this.originalHeight = this.el.nativeElement.style.height || undefined;
    this.renderer.addClass(this.el.nativeElement, `sg-skeleton-${this.effectiveShape}`);
    this.renderer.appendChild(this.el.nativeElement, this.placeholderElement);

    this.setupWidth();

    if (this.effectiveHeight) {
      this.renderer.setStyle(this.el.nativeElement, 'height', this.effectiveHeight);
      this.skeletonHeightApplied = true;
    }

    if (this.effectiveTop) {
      // save original top position
      this.originalTop = this.el.nativeElement.style.top || undefined;
      this.renderer.setStyle(this.el.nativeElement, 'top', this.effectiveTop);
      this.skeletonTopApplied = true;
    }
  }

  /** Deactivates the skeleton by removing styles and restoring original content */
  private deactivate(): void {
    this.renderer.removeClass(this.el.nativeElement, `sg-skeleton-${this.effectiveShape}`);
    this.renderer.removeChild(this.el.nativeElement, this.placeholderElement);

    this.clearWidthAnimation();

    if (this.skeletonWidthApplied) {
      if (this.originalWidth) {
        this.renderer.setStyle(this.el.nativeElement, 'width', this.originalWidth);
      } else {
        this.renderer.removeStyle(this.el.nativeElement, 'width');
      }
      this.skeletonWidthApplied = false;
    }

    if (this.skeletonHeightApplied) {
      if (this.originalHeight) {
        this.renderer.setStyle(this.el.nativeElement, 'height', this.originalHeight);
      } else {
        this.renderer.removeStyle(this.el.nativeElement, 'height');
      }
      this.skeletonHeightApplied = false;
    }

    if (this.skeletonTopApplied) {
      if (this.originalTop) {
        this.renderer.setStyle(this.el.nativeElement, 'top', this.originalTop);
      } else {
        this.renderer.removeStyle(this.el.nativeElement, 'top');
      }
      this.skeletonTopApplied = false;
    }
  }

  /** Sets up the width styling, including animation if array is provided */
  private setupWidth(): void {
    this.clearWidthAnimation();
    if (Array.isArray(this.effectiveWidth) && this.effectiveWidth.length > 0) {
      this.startWidthAnimation();
    } else if (this.effectiveWidth) {
      this.renderer.setStyle(this.el.nativeElement, 'width', this.effectiveWidth);
    }

    if (this.effectiveWidth) {
      this.skeletonWidthApplied = true;
    }
  }

  /** Starts width animation when an array of widths is provided */
  private startWidthAnimation(): void {
    if (Array.isArray(this.effectiveWidth) && this.effectiveWidth.length > 0) {
      const randomizedWidths = [...this.effectiveWidth].sort(() => Math.random() - 0.5);

      // Initial width
      this.currentWidthIndex = 0;
      this.renderer.setStyle(this.el.nativeElement, 'width', randomizedWidths[0]);

      this.widthAnimationInterval = window.setInterval(() => {
        this.currentWidthIndex = (this.currentWidthIndex + 1) % randomizedWidths.length;
        this.renderer.setStyle(this.el.nativeElement, 'width', randomizedWidths[this.currentWidthIndex]);
      }, 1000);
    }
  }

  /** Clears any running width animation */
  private clearWidthAnimation(): void {
    if (this.widthAnimationInterval !== undefined) {
      clearInterval(this.widthAnimationInterval);
      this.widthAnimationInterval = undefined;
    }
  }
}
